import React, { useEffect, useRef, useState, useMemo } from "react";
import { Table, Icon } from "semantic-ui-react";
import { GlobalConstants } from "../../constants/GlobalConstants";
import BCToolTip from "../../ui/BCToolTip";
import { useDrop, useDrag } from "react-dnd";
import { Identifier } from "dnd-core";
import { convertPropFromLegacyToCamelCase } from "../../../helpers/GlobalHelper";

interface DragItem {
  index: number
  id: string
  type: string
}

const ItemTypes = {
  CARD: "card",
};

const LmsTableColumnHeader = ({ ...headerProps }) => {
  const {
    item,
    prop,
    fieldType,
    width,
    isBrokerColor,
    brokerTableHeaderOverrideCSS,
    tableProps: props,
    foundSortable,
    dataSingleLine,
    i,
    myForm,
    editableFields,
    mouseDown,
    mouseUp,
    activeColumn,
    setActiveDragColumn,
    activeResizeStyles,
    handleColumnHeaderDrop,
    setIsEditable,
    isEditable,
    handleContextMenuClick,
    handleColumnFilterClick,
    tableFilterReducerInstance
  } = headerProps;
  const resizeHandleRef = useRef(null as any);
  const filterIconRef = useRef(null as any);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [{ currentViewFilters, currentViewSort }, tableFilterDispatch] = tableFilterReducerInstance;
  const sortedConfig = currentViewSort?.find(cv => cv.prop === prop);
  const sortedDirection = sortedConfig ? currentViewSort?.[0]?.dir : null;
  const widthStyle = width ? `${width}px` : "auto";
  const hasFilter = useMemo(() => {
    return currentViewFilters?.filters?.find(({ filters }) => convertPropFromLegacyToCamelCase(filters[0]?.field) === prop);
  }, [currentViewFilters?.filters]);

  const [{ handlerId, isOver, canDrop }, drop] = useDrop<DragItem,
    void,
    { handlerId: Identifier | null, isOver: boolean, canDrop: boolean }>({
    accept: ItemTypes.CARD,
    drop: () => ({ dropIndex: i, dropProp: prop }) as any,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      };
    },
  });

  const [{ isDragging }, drag]: any = useDrag({
    item: { type: "card", id: i, index: i, prop },
    canDrag: () => activeColumn?.index === undefined,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        handleColumnHeaderDrop({
          prevIndex: item.index,
          newIndex: dropResult.dropIndex,
          isGroupBy:dropResult.isGroupBy,
          prevProp: item.prop,
          newProp: dropResult.dropProp
        });
      }
    },
  });

  useEffect(() => {
    if (isDragging) {
      setActiveDragColumn({ item, prop, index: i });
    }
    else {
      setActiveDragColumn({ item: "", prop: "", index: undefined });
    }
  }, [isDragging]);

  const isActive = isOver && canDrop;

  const handleSortClick = () => {
    tableFilterDispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_SORT,
      payload: [{
        prop,
        field: sortedConfig?.field || prop,
        dir: sortedDirection === "asc" || !sortedDirection ? "desc" : "asc"
      }]
    });
  };

  return <Table.HeaderCell
    className={`lms-table-header-cell ${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}${foundSortable ? " sortable-header" : ""}`}
    onMouseEnter={() => setIsMouseOver(true)}
    onMouseLeave={() => setIsMouseOver(false)}
    singleLine={dataSingleLine}
    id={`table-header-${i}`}
    key={`table-header-${i}`}
    style={{ opacity: isDragging ? .7 : 1 }}
  >
    <div ref={resizeHandleRef} style={{ width: widthStyle, minWidth: "min-content" }}>
       <div
         ref={drag}
         data-handler-id={handlerId}
         style={{ marginLeft: "2px" }}
         className="lms-table-header-container"
         onContextMenu={(e) => handleContextMenuClick(e, i)}
       >
         {(myForm && editableFields[prop]) && <Icon name={GlobalConstants.Icons.EDIT as any} size="large" className="cursor-pointer" onClick={() => setIsEditable(!isEditable)}/>}
         <div onClick={handleSortClick}>
           <span style={{ textOverflow: "ellipsis" }}>{item}</span>
           <label className={"form-input-label"}>{(props.required && item === "Access Level") && <span className="required-asterix">*</span>}
             {(props.tooltip && item === "Access Level") && <BCToolTip tooltip={props.tooltip}/>}</label>
           {sortedDirection === "desc" ? <Icon name='caret down' size="small"/> : sortedDirection === "asc" ? <Icon name='caret up' size="small"/> : null}
         </div>
         <div
           ref={filterIconRef}
           className="lms-table-header-filter-icon-container"
           data-prop={prop}
           onClick={(e) => handleColumnFilterClick(e, filterIconRef, { item, prop, fieldType })}
           style={{ opacity: isMouseOver || hasFilter ? 1 : 0 }}
         >
           <Icon name="filter" />
         </div>
      </div>
      <div
        ref={drop}
        onMouseDown={() => mouseDown(i, null, resizeHandleRef)}
        onMouseUp={mouseUp}
        className={`resize-handle ${activeColumn.index === i || isActive ? activeResizeStyles : ""}`}
      />
    </div>
  </Table.HeaderCell>;
};

export default LmsTableColumnHeader;